import React, { FC, useState, useMemo, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useToast } from '@pcmp/react-providers';
import Button from '@ingka/button';
import ToggleControlWrapper from '@ingka/toggle';
import Tooltip from '@ingka/tooltip';
import downloadIcon from '@ingka/ssr-icon/paths/arrow-down-to-line-small';
import InformationIcon from '@ingka/ssr-icon/paths/information';
import { Data } from '@pcmp/react-coworker-components';
import { useCtaReportPage } from '@/redux/features/cta/hooks';
import CodeSettings, { type CodeSettingsField } from './code-settings';
import CommunicationPurpose from './communication-purpose';
import {
  IFormInputs,
  generateSchema,
} from '@/lib/schemas/create-free-text-form';
import useSchema from '@/lib/hooks/yup';
import { useUser } from './context';

import { useTrackInteraction } from '@/lib/hooks/tracking';
import { FREE_TEXT_COMMUNICATION_PURPOSES } from '@/lib/constants';
import { saveSvgAsPng, saveSvgAsSvg } from '@/lib/utils/file';
import { QRCodeSVG } from 'qrcode.react';

const { Card } = Data;

type DownloadFormat = 'SVG' | 'PNG';

const DOWNLOAD_FORMATS: DownloadFormat[] = ['PNG', 'SVG'];
const DOWNLOAD_FORMATS_HANDLERS: Record<
  DownloadFormat,
  (svg: SVGElement, filename: string) => void
> = {
  PNG: saveSvgAsPng,
  SVG: saveSvgAsSvg,
};

export const getOptimalErrorLevel = (text: string): 'L' | 'M' | 'Q' | 'H' => {
  const length = text.length;
  if (length <= 50) return 'H';
  if (length <= 120) return 'Q';
  if (length <= 200) return 'M';
  return 'L';
};

const QRPreview: FC<{ text: string }> = ({ text }) => {
  // Hooks
  const { t } = useTranslation();
  const svgRef = useRef<SVGSVGElement>(null);
  const [formatIndex, setFormatIndex] = useState(0);
  // Data
  const format = DOWNLOAD_FORMATS[formatIndex];
  const errorLevel = useMemo(() => getOptimalErrorLevel(text || ''), [text]);
  const hasText = Boolean(text && text.trim().length > 0);
  // Event handlers
  const downloadImage = () => {
    if (!svgRef.current) return;
    DOWNLOAD_FORMATS_HANDLERS[format](svgRef.current, t('label.qrCode'));
  };
  const handleFormatChange = (_: any, index: number) => {
    if (!hasText) return;
    setFormatIndex(index);
  };

  return (
    <Card>
      <h3 className="text-xl font-semibold mb-4 text-left">
        {t('label.qrCode')}
      </h3>

      <div
        className={`flex flex-col gap-5 sm:gap-0 items-center ${
          !hasText ? 'opacity-50' : ''
        }`}
      >
        <div className="flex flex-row w-full">
          <QRCodeSVG
            ref={svgRef}
            value={hasText ? text : ' '}
            size={105}
            fgColor={hasText ? '#000000' : '#cccccc'}
            level={hasText ? errorLevel : 'L'}
          />

          <div className="flex flex-col flex-grow ml-4">
            <div className="flex items-center self-stretch">
              <ToggleControlWrapper
                className="flex-grow"
                data-testid="downloadFormatToggle"
                onClick={handleFormatChange}
                activeIndex={formatIndex}
                buttons={DOWNLOAD_FORMATS.map((format) => ({ text: format }))}
                fluid
              />

              <Tooltip
                className="pl-4"
                position="top"
                ssrIcon={InformationIcon}
                //@ts-ignore
                tooltipText={
                  <Trans i18nKey="page.codes.detail.tooltips.formatsInformation" />
                }
              />
            </div>

            <div className="pt-4 w-full">
              <Button
                className="w-full"
                data-testid="downloadImageButton"
                size="medium"
                type="primary"
                iconPosition="leading"
                ssrIcon={downloadIcon}
                text={t('label.download')}
                onClick={hasText ? downloadImage : undefined}
                disabled={!hasText}
              />
            </div>
          </div>
        </div>

        <div className="mt-6 w-full p-3 bg-skapa-neutral-2 rounded-md break-words text-sm">
          {hasText ? text : t('label.freetextPlaceholder')}
        </div>
      </div>
    </Card>
  );
};

export const CreateFreeTextCodeForm: FC = () => {
  const { t } = useTranslation();

  const codeSettingsFields: CodeSettingsField<IFormInputs>[] = [
    {
      name: 'freeText',
      type: 'text' as const,
      fieldType: 'textarea' as const,
      label: t('label.freetext'),
      required: true,
      rows: 3,
      className: 'col-span-2',
    },
  ];

  return (
    <>
      <CommunicationPurpose
        purposes={FREE_TEXT_COMMUNICATION_PURPOSES}
        titleKey={t('label.purpose')}
      />
      <CodeSettings
        showIconSettings={false}
        fields={codeSettingsFields}
        purposes={FREE_TEXT_COMMUNICATION_PURPOSES}
      />
    </>
  );
};

export const CreateFreeTextCodeButton: FC<{
  onShowPreview: (data: IFormInputs) => void;
  disabled?: boolean;
}> = ({ onShowPreview, disabled = false }) => {
  const { user } = useUser();
  const { t } = useTranslation();
  const triggerReportPage = useCtaReportPage();
  const { pushToast } = useToast();
  const {
    getValues,
    formState: { isValid, isDirty },
  } = useFormContext<IFormInputs>();

  const [trackInteraction] = useTrackInteraction();

  const createCode = () => {
    const data = getValues();

    trackInteraction({
      qr: {
        text: data.freeText || '',
        category: data.communicationPurpose || null,
        subcategory: data.subcategory || null,
      },

      user: {
        cdsLocation: user.cdsLocation || '',
        companyName: user.companyName || '',
        country: user.country || '',
        department: user.department || '',
        jobTitle: user.jobTitle || '',
      },

      content: 'freeTextCode',
      type: 'click',
      action: 'create',
    });

    onShowPreview(data);

    pushToast({ message: t('page.createCode.messages.createCode.success') });

    triggerReportPage('createdCode');
  };

  return (
    <Button
      className="w-full lg:w-[327px]"
      iconPosition="leading"
      text={t('page.createCode.submit')}
      type="emphasised"
      disabled={disabled || !isValid || !isDirty}
      onClick={createCode}
      data-testid="create-free-text-code-button"
    />
  );
};

export const CreateFreeTextCodeFormWithSubmit: FC = () => {
  const { t } = useTranslation();
  const [previewData, setPreviewData] = useState<{
    text: string;
    codeName: string;
  }>({
    text: '',
    codeName: t('label.qrCode'),
  });
  const [hasCreatedCode, setHasCreatedCode] = useState(false);

  const resolver = useSchema(() => generateSchema());
  const form = useForm<IFormInputs>({
    mode: 'all',
    resolver,
    defaultValues: {
      communicationPurpose: '',
      subcategory: '',
      freeText: '',
    },
    reValidateMode: 'onChange',
    criteriaMode: 'all',
  });

  const handleShowPreview = (data: IFormInputs) => {
    setPreviewData({ text: data.freeText, codeName: t('label.qrCode') });
    setHasCreatedCode(true);
    form.reset(data);
  };

  const freeTextValue = form.watch('freeText');
  React.useEffect(() => {
    if (hasCreatedCode && freeTextValue.trim().length > 0) {
      setHasCreatedCode(false);
    }
  }, [freeTextValue, hasCreatedCode]);

  return (
    <FormProvider {...form}>
      <div className="w-full flex flex-col lg:flex-row gap-6">
        <Card className="w-full lg:w-1/2 mb-6 lg:mb-0">
          <CreateFreeTextCodeForm />
          <div className="flex justify-end mt-6">
            <CreateFreeTextCodeButton
              onShowPreview={handleShowPreview}
              disabled={hasCreatedCode}
            />
          </div>
        </Card>

        <div className="w-full lg:w-1/2">
          <QRPreview text={previewData.text} />
        </div>
      </div>
    </FormProvider>
  );
};
